import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Crown } from 'lucide-react';
import { getPaymentStatus } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/UI';

const MAX_ATTEMPTS = 10;
const INTERVAL_MS = 2000;

export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempt = 0;
    const poll = async () => {
      attempt += 1;
      setAttempts(attempt);
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === 'paid') {
          setStatus('paid');
          await refresh();
          return;
        }
        if (res.payment_status === 'failed' || res.payment_status === 'expired') {
          setStatus('failed');
          return;
        }
      } catch (e) {}
      if (attempt >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      timerRef.current = setTimeout(poll, INTERVAL_MS);
    };
    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'var(--lf-bg)' }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl p-8 text-center"
        style={{ border: '1px solid var(--lf-line)', boxShadow: '0 20px 50px rgba(15,23,42,.08)' }}
      >
        {status === 'checking' && (
          <>
            <Loader2 className="animate-spin mx-auto" size={32} style={{ color: 'var(--lf-blue)' }} />
            <h2 className="text-xl font-bold mt-4 mb-2">Confirming your payment…</h2>
            <p className="text-sm" style={{ color: 'var(--lf-muted)' }}>
              Attempt {attempts} / {MAX_ATTEMPTS}
            </p>
          </>
        )}
        {status === 'paid' && (
          <>
            <Crown size={32} style={{ color: '#f59e0b', margin: '0 auto 10px' }} />
            <h2 className="text-2xl font-bold mb-2">Welcome to Pro</h2>
            <p className="text-sm mb-5" style={{ color: 'var(--lf-muted)' }}>
              Your subscription is active. All premium features are now unlocked.
            </p>
            <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
              Go to dashboard
            </Button>
          </>
        )}
        {(status === 'failed' || status === 'timeout' || status === 'error') && (
          <>
            <XCircle size={32} style={{ color: '#dc2626', margin: '0 auto 10px' }} />
            <h2 className="text-xl font-bold mb-2">Payment not confirmed</h2>
            <p className="text-sm mb-5" style={{ color: 'var(--lf-muted)' }}>
              {status === 'timeout'
                ? 'We did not receive confirmation in time. If you were charged, it will reflect shortly.'
                : 'Something went wrong processing your payment. You were not charged.'}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="secondary" onClick={() => navigate('/', { replace: true })}>
                Back to app
              </Button>
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-bold rounded-lg no-underline"
                style={{ background: 'var(--lf-blue)', color: '#fff' }}
              >
                Try again
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'var(--lf-bg)' }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl p-8 text-center"
        style={{ border: '1px solid var(--lf-line)', boxShadow: '0 20px 50px rgba(15,23,42,.08)' }}
      >
        <CheckCircle2 size={28} style={{ color: 'var(--lf-muted)', margin: '0 auto 10px' }} />
        <h2 className="text-xl font-bold mb-2">Checkout cancelled</h2>
        <p className="text-sm mb-5" style={{ color: 'var(--lf-muted)' }}>
          No worries — you were not charged. You can upgrade whenever you’re ready.
        </p>
        <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
          Back to app
        </Button>
      </div>
    </div>
  );
}
